import { useCallback, useEffect, useState } from 'react';
import '../App.css';
import '../Assets/css/Main.css';
import { Row, Col } from 'reactstrap';
import base from '../Services/BaseService';
import BudgetBalanceModal from './BudgetBalanceModal';
import DashboardActions from './Dashboard/DashboardActions';
import DashboardAccounts from './Dashboard/DashboardAccounts';
import DashboardBudgets from './Dashboard/DashboardBudgets';
import DashboardInvestments from './Dashboard/DashboardInvestments';
import DashboardTransactions from './Dashboard/DashboardTransactions';

const Dashboard = () => {
    const [fieldData, setFieldData] = useState({});
    const [budgetData, setBudgetData] = useState([]);
    const [expenseData, setExpenseData] = useState([]);
    const [investmentData, setInvestmentData] = useState([]);
    const [bankData, setBankData] = useState([]);
    const [selectedBudget, setSelectedBudget] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [budgetLoading, setBudgetLoading] = useState(true);
    const [transactionsLoading, setTransactionsLoading] = useState(true);
    const [accountsLoading, setAccountsLoading] = useState(true);
    const [investmentsLoading, setInvestmentsLoading] = useState(true);

    const refreshSummary = useCallback(async () => {
        setSummaryLoading(true);
        try {
            setFieldData(await base.getAll('Dashboard/FieldData'));
        } catch (error) {
            console.error('Dashboard summary could not be loaded', error);
        } finally {
            setSummaryLoading(false);
        }
    }, []);

    const refreshBudgetRows = useCallback(async () => {
        setBudgetLoading(true);
        try {
            setBudgetData(await base.getAll('Dashboard/Budget'));
        } catch (error) {
            console.error('Dashboard budgets could not be loaded', error);
        } finally {
            setBudgetLoading(false);
        }
    }, []);

    const refreshBudgets = useCallback(() => Promise.all([
        refreshSummary(),
        refreshBudgetRows(),
    ]), [refreshBudgetRows, refreshSummary]);

    const refreshTransactions = useCallback(async () => {
        setTransactionsLoading(true);
        try {
            setExpenseData(await base.getAll('Dashboard/expense?Month=6'));
        } catch (error) {
            console.error('Dashboard transactions could not be loaded', error);
        } finally {
            setTransactionsLoading(false);
        }
    }, []);

    const refreshAccounts = useCallback(async () => {
        setAccountsLoading(true);
        try {
            setBankData(await base.getAll('Dashboard/Bank'));
        } catch (error) {
            console.error('Dashboard accounts could not be loaded', error);
        } finally {
            setAccountsLoading(false);
        }
    }, []);

    const refreshInvestments = useCallback(async () => {
        setInvestmentsLoading(true);
        try {
            setInvestmentData(await base.getAll('Dashboard/Investment'));
        } catch (error) {
            console.error('Dashboard investments could not be loaded', error);
        } finally {
            setInvestmentsLoading(false);
        }
    }, []);

    const refreshDashboard = useCallback(() => Promise.all([
        refreshBudgets(),
        refreshTransactions(),
        refreshAccounts(),
        refreshInvestments(),
    ]), [refreshAccounts, refreshBudgets, refreshInvestments, refreshTransactions]);

    useEffect(() => {
        refreshDashboard();
    }, [refreshDashboard]);

    return (
        <div>
            {selectedBudget && (
                <BudgetBalanceModal
                    budget={selectedBudget}
                    closeModal={() => setSelectedBudget(null)}
                    onSaved={refreshBudgets}
                />
            )}
            <DashboardActions onRefresh={refreshDashboard} />

            <div className="pt-2 pb-2 res-padding" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <Row>
                    <Col xs="12" xl="5">
                        <DashboardBudgets
                            fieldData={fieldData}
                            budgetData={budgetData}
                            summaryLoading={summaryLoading}
                            budgetLoading={budgetLoading}
                            onRefresh={refreshBudgets}
                            onManageBalance={setSelectedBudget}
                        />
                    </Col>
                    <Col xs="12" xl="7">
                        <DashboardAccounts
                            fieldData={fieldData}
                            bankData={bankData}
                            summaryLoading={summaryLoading}
                            accountsLoading={accountsLoading}
                        />
                        <DashboardInvestments data={investmentData} loading={investmentsLoading} />
                        <DashboardTransactions data={expenseData} loading={transactionsLoading} />
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Dashboard;
